import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchContent } from "@/lib/api";
import { FadeUp, RevealImage } from "@/components/Reveal";

export default function Journal() {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    fetchContent("/journal").then(setArticles);
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set(articles.map((a) => a.category))],
    [articles]
  );
  const visible = active === "All" ? articles : articles.filter((a) => a.category === active);
  const [featured, ...rest] = visible;

  return (
    <main data-testid="journal-page" className="pt-24">
      <section className="max-w-[1500px] mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-14">
        <FadeUp>
          <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-6">The Journal</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-ink">
            Notes from the Studio
          </h1>
          <p className="mt-6 max-w-xl text-base font-light text-ink/65 leading-relaxed">
            Essays on Bengali folk culture, craft research, textile traditions and the making of collections.
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-12 flex flex-wrap gap-3">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`journal-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setActive(cat)}
              className={`px-5 py-2.5 text-[10px] font-medium tracking-[0.22em] uppercase border transition-all duration-500 ${
                active === cat
                  ? "bg-ink text-paper border-ink"
                  : "border-ink/25 text-ink/60 hover:border-ink hover:text-ink"
              }`}
            >
              {cat}
            </button>
          ))}
        </FadeUp>
      </section>

      <section className="max-w-[1500px] mx-auto px-5 md:px-10 pb-28">
        {featured && (
          <FadeUp>
            <Link
              to={`/journal/${featured.slug}`}
              data-testid={`journal-featured-${featured.slug}`}
              data-cursor="VIEW"
              className="group grid grid-cols-1 md:grid-cols-12 gap-8 items-center mb-20"
            >
              <div className="md:col-span-8">
                <RevealImage src={featured.image} alt={featured.title} className="aspect-[16/9]" />
              </div>
              <div className="md:col-span-4">
                <p className="text-[10px] tracking-[0.3em] uppercase text-alta mb-4">
                  Featured · {featured.category}
                </p>
                <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink group-hover:text-alta transition-colors duration-500 leading-[1.1]">
                  {featured.title}
                </h2>
                <p className="mt-5 text-sm font-light text-ink/65 leading-relaxed">{featured.excerpt}</p>
                <p className="mt-5 text-[10px] tracking-[0.25em] uppercase text-ink/40">{featured.date}</p>
              </div>
            </Link>
          </FadeUp>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {rest.map((a, i) => (
            <FadeUp key={a.id} delay={(i % 3) * 0.08}>
              <Link to={`/journal/${a.slug}`} data-testid={`journal-card-${a.slug}`} data-cursor="VIEW" className="group block">
                <RevealImage src={a.image} alt={a.title} className="aspect-[4/3]" />
                <p className="mt-5 text-[10px] tracking-[0.3em] uppercase text-terracotta">{a.category}</p>
                <h3 className="mt-3 font-serif text-2xl font-light text-ink group-hover:text-alta transition-colors duration-500 leading-snug">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm font-light text-ink/60 leading-relaxed line-clamp-3">{a.excerpt}</p>
                <p className="mt-4 text-[10px] tracking-[0.25em] uppercase text-ink/40">{a.date}</p>
              </Link>
            </FadeUp>
          ))}
        </div>

        {articles.length === 0 && (
          <p className="text-ink/50 font-light py-10" data-testid="journal-empty">The journal is being bound…</p>
        )}
      </section>
    </main>
  );
}
